import { useCallback, useEffect, useMemo, useState } from 'react'

import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import {
  ProgressAiBatch,
  ReviewTask,
  REVIEW_TASK_PAGE_SIZE,
  getReviewTaskAiBatch,
  listReviewTasks,
  setReviewTaskStatus,
  startReviewTaskAiBatch,
  stopReviewTaskAiBatch,
} from '../../../api/reviewTasks'
import { useNotifications } from '../../../contexts/notificationProvider'
import { useGeneratedPaths } from '../../../hooks/useGeneratedPaths'
import { blue } from '../../../styles/colors'
import { LoadingIndicator } from '../../common/LoadingIndicator'

import {
  formatAiBatchEta,
  formatAiBatchStatus,
  formatReviewTaskProgress,
  formatUsdAmount,
  isAiBatchFinished,
  isAiBatchRunning,
} from './reviewTaskFormat'
import {
  ActionButton,
  EmptyState,
  LoadMoreWrap,
  Table,
  TableScroll,
  Td,
  Th,
} from './styles'

const StatusCell = styled.div`
  position: relative;
  cursor: default;
  white-space: nowrap;
`

const HoverCard = styled.div`
  display: none;
  position: absolute;
  z-index: 5;
  left: 0;
  top: 100%;
  margin-top: 4px;
  min-width: 220px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  font-size: 12px;
  color: #333;
  white-space: normal;

  ${StatusCell}:hover & {
    display: block;
  }
`

const IconBtn = styled.button`
  border: none;
  background: ${blue};
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const StopBtn = styled(IconBtn)`
  border-radius: 4px;
  background: ${blue};
`

const TaskLink = styled.button`
  border: none;
  background: none;
  color: ${blue};
  cursor: pointer;
  padding: 0;
  font: inherit;
`

interface ReviewAiAssistTabProps {
  projectId: string
}

export function ReviewAiAssistTab({ projectId }: ReviewAiAssistTabProps) {
  const history = useHistory()
  const { generateReviewTaskPath, generateAdminReviewAiPath } =
    useGeneratedPaths()
  const { addNotification } = useNotifications()

  const [tasks, setTasks] = useState<ReviewTask[]>([])
  const [batches, setBatches] = useState<Record<number, ProgressAiBatch | null>>(
    {},
  )
  const [loading, setLoading] = useState(true)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [busyTaskId, setBusyTaskId] = useState<number | null>(null)

  const loadTasks = useCallback(
    async (nextOffset = 0, append = false) => {
      setLoading(true)
      try {
        const response = await listReviewTasks(projectId, {
          ai_assist: true,
          limit: REVIEW_TASK_PAGE_SIZE,
          offset: nextOffset,
        })
        const rows = response.data || []
        setTasks(prev => (append ? [...prev, ...rows] : rows))
        setOffset(nextOffset + rows.length)
        setHasMore(rows.length >= REVIEW_TASK_PAGE_SIZE)
        setBatches(prev => {
          const next = append ? { ...prev } : {}
          rows.forEach(task => {
            next[task.id] = task.ai_batch ?? next[task.id] ?? null
          })
          return next
        })
      } catch (err: unknown) {
        const message =
          (err as { response?: { data?: { detail?: string } }; message?: string })
            ?.response?.data?.detail ||
          (err as { message?: string })?.message ||
          'Failed to load AI Assist tasks'
        addNotification(message, 'error')
      } finally {
        setLoading(false)
      }
    },
    [addNotification, projectId],
  )

  useEffect(() => {
    void loadTasks(0, false)
  }, [loadTasks])

  const runningTaskIds = useMemo(
    () =>
      Object.entries(batches)
        .filter(([, batch]) => isAiBatchRunning(batch))
        .map(([id]) => Number(id)),
    [batches],
  )

  useEffect(() => {
    if (!runningTaskIds.length) return undefined
    const id = window.setInterval(() => {
      void Promise.all(
        runningTaskIds.map(async taskId => {
          try {
            const result = await getReviewTaskAiBatch(projectId, taskId)
            setBatches(prev => ({ ...prev, [taskId]: result.batch }))
          } catch {
            /* ignore poll errors */
          }
        }),
      )
    }, 2000)
    return () => window.clearInterval(id)
  }, [projectId, runningTaskIds])

  const onStartBatch = async (task: ReviewTask) => {
    setBusyTaskId(task.id)
    try {
      const batch = batches[task.id]
      const result = await startReviewTaskAiBatch(projectId, task.id, {
        verify_installed: batch?.verify_installed ?? false,
        send_examples: batch?.send_examples ?? true,
      })
      setBatches(prev => ({ ...prev, [task.id]: result.batch }))
      addNotification(`Started AI batch for task ${task.id}`, 'success')
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to start batch'
      addNotification(message, 'error')
    } finally {
      setBusyTaskId(null)
    }
  }

  const onStopBatch = async (task: ReviewTask) => {
    setBusyTaskId(task.id)
    try {
      const result = await stopReviewTaskAiBatch(projectId, task.id)
      setBatches(prev => ({ ...prev, [task.id]: result.batch }))
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to stop batch'
      addNotification(message, 'error')
    } finally {
      setBusyTaskId(null)
    }
  }

  const onStartReview = async (task: ReviewTask) => {
    const batch = batches[task.id]
    if (!isAiBatchFinished(batch)) {
      addNotification('Finish the AI batch before starting review.', 'error')
      return
    }
    try {
      await setReviewTaskStatus(projectId, task.id, 'in_progress')
      history.push(generateReviewTaskPath(projectId, String(task.id)))
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ||
        (err as { message?: string })?.message ||
        'Failed to start task'
      addNotification(message, 'error')
    }
  }

  if (loading && tasks.length === 0) return <LoadingIndicator />

  return (
    <>
      <TableScroll>
        <Table>
          <thead>
            <tr>
              <Th>Task ID</Th>
              <Th>Progress</Th>
              <Th>Levels</Th>
              <Th>Trackers</Th>
              <Th>Assignee</Th>
              <Th>Batch Run</Th>
              <Th>Batch Status</Th>
              <Th>Batch Edit</Th>
              <Th>Act</Th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 && (
              <tr>
                <Td colSpan={9}>
                  <EmptyState>
                    No AI Assist tasks. Enable AI-Assist on a task in the main
                    list first.
                  </EmptyState>
                </Td>
              </tr>
            )}
            {tasks.map(task => {
              const batch = batches[task.id]
              const running = isAiBatchRunning(batch)
              const finished = isAiBatchFinished(batch)
              const busy = busyTaskId === task.id
              return (
                <tr key={task.id}>
                  <Td>
                    <TaskLink
                      type="button"
                      onClick={() =>
                        history.push(
                          generateAdminReviewAiPath(projectId, String(task.id)),
                        )
                      }
                    >
                      {task.id}
                    </TaskLink>
                  </Td>
                  <Td>
                    {formatReviewTaskProgress(task)}
                  </Td>
                  <Td>{task.floors_label}</Td>
                  <Td>{task.trackers_label}</Td>
                  <Td>{task.assignees_label}</Td>
                  <Td>
                    {running ? (
                      <StopBtn
                        type="button"
                        title="Stop batch"
                        disabled={busy}
                        onClick={() => void onStopBatch(task)}
                      >
                        ■
                      </StopBtn>
                    ) : (
                      <IconBtn
                        type="button"
                        title="Run batch"
                        disabled={busy}
                        onClick={() => void onStartBatch(task)}
                      >
                        ▶
                      </IconBtn>
                    )}
                  </Td>
                  <Td>
                    <StatusCell>
                      {formatAiBatchStatus(batch)}
                      {batch ? (
                        <HoverCard>
                          <div>
                            {batch.percent_complete}% · {batch.points_done}/
                            {batch.points_total} points completed
                          </div>
                          <div>Spent: {formatUsdAmount(batch.cost_usd, 2)}</div>
                          <div>
                            Forecast:{' '}
                            {batch.forecast_cost_usd != null
                              ? formatUsdAmount(batch.forecast_cost_usd, 2)
                              : '—'}
                          </div>
                          <div>
                            ETA: {formatAiBatchEta(batch.eta_seconds) || '—'}
                          </div>
                          <div>
                            Failed: {batch.points_failed} · Skipped:{' '}
                            {batch.points_skipped}
                          </div>
                        </HoverCard>
                      ) : null}
                    </StatusCell>
                  </Td>
                  <Td>
                    <ActionButton
                      type="button"
                      onClick={() =>
                        history.push(
                          generateAdminReviewAiPath(projectId, String(task.id)),
                        )
                      }
                    >
                      Edit
                    </ActionButton>
                  </Td>
                  <Td>
                    <ActionButton
                      type="button"
                      disabled={!finished}
                      title={
                        finished
                          ? 'Start review'
                          : 'Complete AI batch before starting'
                      }
                      onClick={() => void onStartReview(task)}
                    >
                      Start
                    </ActionButton>
                  </Td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </TableScroll>
      {hasMore ? (
        <LoadMoreWrap>
          <ActionButton
            type="button"
            disabled={loading}
            onClick={() => void loadTasks(offset, true)}
          >
            Load more
          </ActionButton>
        </LoadMoreWrap>
      ) : null}
    </>
  )
}
